/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import {BeamInputLabel, BeamInputLabelProps} from './BeamInputLabel';
import {useFormikContext} from 'formik';
import {Box, FormControlLabel, Switch} from '@mui/material';
import React from 'react';
import {styled} from '@mui/material/styles';

export interface BeamSwitchProps {
  name: string;

  trueLabel?: string;
  falseLabel?: string;
}

const BeamStyledSwitch = styled(Switch)(({ theme }) => ({
  padding: 8,
  '& .Mui-checked+.MuiSwitch-track': {
    backgroundColor: '#00C2DE !important',
    opacity: '1 !important',
  },
  '& .MuiSwitch-track': {
    backgroundColor: '#9FAFBC',
    opacity: 1,
    borderRadius: 22 / 2,
    '&::before, &::after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16,
    },
    '&::before': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 12 12"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" fill-rule="evenodd" clip-rule="evenodd" d="M4.49999 8.08501L2.41499 6.00001L1.70499 6.70501L4.49999 9.50001L10.5 3.50001L9.79499 2.79501L4.49999 8.08501Z"/></svg>')`,
      left: 12,
    },
    '&::after': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 12 12"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main),
      )}" fill-rule="evenodd" clip-rule="evenodd" d="M9.5 3.205L8.795 2.5L6 5.295L3.205 2.5L2.5 3.205L5.295 6L2.5 8.795L3.205 9.5L6 6.705L8.795 9.5L9.5 8.795L6.705 6L9.5 3.205Z" /></svg>')`,
      right: 12,
    },
  },
  '& .MuiSwitch-thumb': {
    backgroundColor: '#1C262F',
    boxShadow: 'none',
    width: 16,
    height: 16,
    margin: 2,
  },
}));

export const BeamSwitch = ({
  name,
  label,
  optional,
  required,
  trueLabel,
  falseLabel,
}: BeamSwitchProps & Omit<BeamInputLabelProps, 'htmlFor'>) => {
  const formikContext = useFormikContext();
  const curValue = formikContext?.values?.[name] ?? false;
  let labelText = '';
  if (curValue === true && trueLabel !== undefined) {
    labelText = trueLabel;
  } else if (curValue === false && falseLabel !== undefined) {
    labelText = falseLabel;
  }

  return (
    <Box display={'flex'} flexDirection={'column'} gap={'8px'}>
      <BeamInputLabel label={label} optional={optional} required={required} htmlFor={name} />

      <FormControlLabel
        sx={{
          marginLeft: '-8px',
          marginTop: '-8px',
        }}
        control={
          <BeamStyledSwitch
            id={name}
            name={name}
            checked={curValue}
            onChange={e => formikContext?.setFieldValue(name, e.target.checked)}
            disabled={formikContext.isSubmitting}
          />
        }
        label={labelText}
      />
    </Box>
  );
};
